import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { RobotServicePurchaseComponent } from 'src/app/routes/robot-service/purchase/robot-service-purchase.component';
import { PaymentIframeModalComponent } from 'src/app/shared/components/payment-iframe-modal/payment-iframe-modal.component';
import { RobotOrderHistoryModalComponent } from 'src/app/shared/components/robot-order-history-modal/robot-order-history-modal.component';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import {
  PaymentOrderType,
  buildPaymentResultUrl,
  formatCountdown,
  getPaymentRemainingMs,
  storePaymentSessionSnapshot
} from 'src/app/shared/utils/payment-flow.util';
import { ConstantUtil } from 'src/app/util/constant.util';

interface SelectedPricing {
  pricingConfigId: string;
  pricingItemId: string;
  pricingCode: string;
}

const ROBOT_PRICING_CODE = 'AI_LIMIT';

@Component({
  selector: 'robot-service-selector',
  standalone: true,
  templateUrl: './robot-service-selector.component.html',
  styleUrls: ['./robot-service-selector.component.less'],
  imports: [CommonModule, FormsModule, I18nPipe, NzTagModule, NzIconModule, NzSpinModule, NzButtonModule, TestIdDirective]
})
export class RobotServiceSelectorComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpService);
  private readonly modalRef = inject(NzModalRef);
  private readonly modalService = inject(NzModalService);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  readonly modalData: any = inject(NZ_MODAL_DATA, { optional: true }) || {};

  loading = false;
  robotList: any[] = [];
  displayList: any[] = [];

  searchValue = '';
  selectedRobotId: string | null = null;
  mountedRobotId: string | null = null;
  currentServiceNumberId: string | null = null;
  selectedRobot: any | null = null;
  trialUsed = false;
  trialAvailable = false;
  private aipoolTenantId = '';
  renamingRobotId: string | null = null;
  renameInputValue = '';
  renamingSubmittingRobotId: string | null = null;
  deletingRobotId: string | null = null;
  cancelingOrderId: string | null = null;
  attachingRobotId: string | null = null;
  detachingRobotId: string | null = null;
  now = Date.now();
  private countdownTimer: number | null = null;
  typeMap: Record<string, { label: string; color: string }> = {
    Gpt: { label: this.i18n.fanyi('robot-service.type.gpt'), color: '#1890ff' },
    Cbm: { label: this.i18n.fanyi('robot-service.type.cbm'), color: '#13c2c2' },
    VirtualPersona: { label: this.i18n.fanyi('robot-service.identity.virtual-persona'), color: '#7c3aed' },
    VirtualEmployee: { label: this.i18n.fanyi('robot-service.identity.virtual-employee'), color: '#0891b2' }
  };

  ngOnInit(): void {
    this.mountedRobotId = this.modalData?.selectedRobotId || this.modalData?.robotId || null;
    this.selectedRobotId = this.mountedRobotId;
    this.currentServiceNumberId = this.modalData?.currentServiceNumberId || this.modalData?.serviceNumberId || null;
    this.loadBalanceSummary();
    this.loadRobotList();
    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  private loadBalanceSummary(): void {
    this.http.post(ConstantUtil.ADMIN_ROBOT_ORDER_V1_POINTS, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) return;
        this.aipoolTenantId = this.resolveAipoolTenantId(res?.data);
      },
      error: () => {}
    });
  }

  loadRobotList(): void {
    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_ROBOT_V1_SELECTABLE_LIST, { pricingCode: ROBOT_PRICING_CODE })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: res => {
          if (res?.success) {
            const raw = res.data ?? {};
            const list = Array.isArray(raw?.items) ? raw.items : [];
            const trialUsed = raw?.trialUsed === true;
            this.robotList = list;
            this.trialUsed = trialUsed;
            this.trialAvailable = !trialUsed;
            const onCountChange = this.modalData?.onCountChange;
            if (typeof onCountChange === 'function') {
              onCountChange(this.robotList.length);
            }
            if (this.selectedRobotId) {
              this.selectedRobot = this.robotList.find(r => this.getRobotInstanceKey(r) === String(this.selectedRobotId)) || null;
            }
            this.applyFilter();
          }
        },
        error: () => {
          // 静默失败，保持选择控件只读可用
        }
      });
  }

  getRobotTypeInfo(code: string, identityType?: string): { label: string; color: string } {
    const preferred = identityType || code;
    return this.typeMap[preferred] || { label: preferred, color: 'default' };
  }

  isVirtualPersona(robot: any): boolean {
    const preferred = String(robot?.identityType || robot?.type || '').trim();
    return preferred === 'VirtualPersona';
  }

  isVirtualEmployee(robot: any): boolean {
    const preferred = String(robot?.identityType || robot?.type || '').trim();
    return preferred === 'VirtualEmployee';
  }

  getRobotTypeTagColor(robot: any): string {
    if (this.isVirtualPersona(robot) || this.isVirtualEmployee(robot)) return '';
    return this.getRobotTypeInfo(robot?.type, robot?.identityType).color;
  }

  getLanguageLabel(code?: string): string {
    switch (code) {
      case 'ZH_TW':
        return this.i18n.fanyi('robot-service.language.zh-tw');
      case 'ZH_CN':
        return this.i18n.fanyi('robot-service.language.zh-cn');
      case 'EN_US':
        return this.i18n.fanyi('robot-service.language.en-us');
      default:
        return code || '';
    }
  }

  isCurrent(robot: any): boolean {
    const currentId = this.mountedRobotId;
    return !!currentId && this.getRobotInstanceKey(robot) === String(currentId);
  }

  isSelected(robot: any): boolean {
    const selectedId = this.selectedRobotId;
    return !!selectedId && this.getRobotInstanceKey(robot) === String(selectedId);
  }

  isTrial(robot: any): boolean {
    return !!robot?.trial;
  }

  isExpired(robot: any): boolean {
    const now = Date.now();
    const expire = this.getExpireTimestamp(robot);
    return !!expire && expire <= now;
  }

  isDisabled(robot: any): boolean {
    return !this.hasValidExpireTime(robot) && this.isCanceledOrder(robot);
  }

  getStatusLabel(robot: any): string {
    if (this.isTrial(robot)) {
      return this.i18n.fanyi('robot-service.my.trial');
    }
    if (this.hasPendingPayment(robot)) {
      return this.i18n.fanyi('robot-service.order.need-pay');
    }
    if (this.isCurrent(robot)) {
      return this.i18n.fanyi('robot-service.my.current-used');
    }
    if (this.isExpired(robot)) {
      return this.i18n.fanyi('robot-service.my.expired');
    }
    if (this.isDisabled(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.disabled');
    }
    return this.i18n.fanyi('robot-service.my.available');
  }

  getStatusColor(robot: any): string {
    if (this.isTrial(robot)) return 'gold';
    if (this.hasPendingPayment(robot)) return 'red';
    if (this.isCurrent(robot)) return 'processing';
    if (this.isExpired(robot)) return 'red';
    if (this.isDisabled(robot)) return 'default';
    return 'green';
  }

  getRemainingDays(robot: any): number | null {
    const expire = this.getExpireTimestamp(robot);
    if (!expire) return null;
    const diff = expire - Date.now();
    if (diff <= 0) return 0;
    return Math.ceil(diff / (24 * 60 * 60 * 1000));
  }

  getOrderStatusTag(robot: any): { show: boolean; color: string; label: string } {
    const orderStatus = String(robot?.latestOrderStatus ?? '').toLowerCase();
    if (orderStatus === 'failed') {
      return { show: true, color: 'red', label: this.i18n.fanyi('robot-service.order.failed') };
    }
    return { show: false, color: 'default', label: '' };
  }

  isCanceledOrder(robot: any): boolean {
    return [robot?.paymentStatus, robot?.latestOrderStatus]
      .map(value =>
        String(value ?? '')
          .trim()
          .toLowerCase()
      )
      .some(status => status === 'canceled' || status === 'cancelled');
  }

  hasPendingPayment(robot: any): boolean {
    return robot?.needPay === true;
  }

  getPendingPaymentCountdown(robot: any): string {
    const remainingMs = getPaymentRemainingMs(robot, this.now);
    if (remainingMs === null) return '';
    if (remainingMs === 0) {
      return this.i18n.fanyi('payment.result.countdown.expired');
    }
    return formatCountdown(remainingMs);
  }

  hasUsablePendingPayment(robot: any): boolean {
    if (!this.hasPendingPayment(robot)) return false;
    const remainingMs = getPaymentRemainingMs(robot, this.now);
    if (remainingMs !== null) {
      return remainingMs > 0;
    }
    if (this.hasValidExpireTime(robot) && !this.isExpired(robot)) {
      return true;
    }
    const latestOrderStatus = String(robot?.latestOrderStatus ?? '')
      .trim()
      .toLowerCase();
    return !['failed', 'canceled', 'expired', 'closed', 'replaced'].includes(latestOrderStatus);
  }

  canPayPendingOrder(robot: any): boolean {
    const remainingMs = getPaymentRemainingMs(robot, this.now);
    if (remainingMs !== null) {
      return remainingMs > 0;
    }
    const latestOrderStatus = String(robot?.latestOrderStatus ?? '')
      .trim()
      .toLowerCase();
    return !['failed', 'canceled', 'expired', 'closed', 'replaced'].includes(latestOrderStatus);
  }

  canCancelPendingOrder(robot: any): boolean {
    return this.hasPendingPayment(robot) && !!this.getRobotOrderId(robot);
  }

  isCancelingPendingOrder(robot: any): boolean {
    const orderId = this.getRobotOrderId(robot);
    return !!orderId && this.cancelingOrderId === orderId;
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.countdownTimer = window.setInterval(() => {
      this.now = Date.now();
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  isAttachable(robot: any): boolean {
    if (this.hasPendingPayment(robot)) return false;
    return (
      !this.isCurrent(robot) &&
      !this.isBoundToCurrentService(robot) &&
      !this.isExpired(robot) &&
      !this.isDisabled(robot) &&
      !this.isBoundElsewhere(robot)
    );
  }

  canCancelAttach(robot: any): boolean {
    return (this.isCurrent(robot) || this.isBoundToCurrentService(robot)) && !this.isExpired(robot) && !this.isDisabled(robot);
  }

  hasValidExpireTime(robot: any): boolean {
    return !!this.getExpireTimestamp(robot);
  }

  getCountText(): string {
    const tpl = this.i18n.fanyi('robot-service.my.subtitle') || '';
    return tpl.replace('{count}', String(this.robotList.length));
  }

  onSearchChange(): void {
    this.applyFilter();
  }

  applyFilter(): void {
    const kw = (this.searchValue || '').trim().toLowerCase();
    if (!kw) {
      this.displayList = [...this.robotList];
      return;
    }
    this.displayList = this.robotList.filter(r => {
      const name = (r?.name || '').toLowerCase();
      const id = String(r?.robotId || '').toLowerCase();
      return name.includes(kw) || id.includes(kw);
    });
  }

  select(robot: any): void {
    this.selectedRobotId = this.getRobotInstanceKey(robot);
    this.selectedRobot = robot || null;
  }

  private getRobotInstanceKey(robot: any): string {
    return String(robot?.robotId ?? '');
  }

  private getExpireTimestamp(robot: any): number | null {
    const raw = Number(robot?.expireTime ?? 0);
    if (!Number.isFinite(raw) || raw <= 0) return null;
    return raw;
  }

  async attach(robot: any): Promise<void> {
    if (!this.isAttachable(robot)) {
      if (this.isBoundElsewhere(robot)) {
        this.message.warning(
          this.i18n
            .fanyi('servicenumber.robot.attach.occupied')
            .replace('{serviceNumberName}', this.getBindingServiceNumberName(robot) || this.getBindingServiceNumberId(robot) || '-')
        );
      }
      return;
    }
    const robotKey = this.getRobotInstanceKey(robot);
    const cb = this.modalData?.onAttachRobot;
    if (typeof cb === 'function') {
      this.attachingRobotId = robotKey;
      try {
        const ok = await Promise.resolve(
          cb({
            ...robot,
            id: robot?.robotServiceId,
            robotSubId: this.getRobotInstanceKey(robot) || null,
            robotId: robot?.robotId ?? this.getRobotInstanceKey(robot) ?? null,
            robotServiceId: robot?.robotServiceId ?? null
          })
        );
        if (ok) {
          this.modalRef.close({ refresh: true });
        }
      } finally {
        this.attachingRobotId = null;
      }
      return;
    }
    this.modalRef.close({
      ...robot,
      id: robot?.robotServiceId,
      robotSubId: this.getRobotInstanceKey(robot) || null,
      robotId: robot?.robotId ?? this.getRobotInstanceKey(robot) ?? null,
      robotServiceId: robot?.robotServiceId
    });
  }

  async cancelAttach(robot: any): Promise<void> {
    const robotKey = this.getRobotInstanceKey(robot);
    const cb = this.modalData?.onDetachRobot || this.modalData?.onCancelAttach;
    if (typeof cb === 'function') {
      this.detachingRobotId = robotKey;
      try {
        const ok = await Promise.resolve(
          cb({
            robotId: robot?.robotId ?? null,
            robotServiceId: robot?.robotServiceId ?? null
          })
        );
        if (ok) {
          this.modalRef.close({ refresh: true });
        }
      } finally {
        this.detachingRobotId = null;
      }
      return;
    }
    this.modalRef.close({
      action: 'unbind',
      robotId: robot?.robotId ?? null,
      robotServiceId: robot?.robotServiceId ?? null
    });
  }

  openPurchase(mode: 'buy' | 'renew', robot?: any, initialStep: 'packages' | 'payment' = 'packages'): void {
    const purchaseModal = this.modalService.create({
      nzTitle: this.i18n.fanyi(mode === 'renew' ? 'robot-service.purchase.title.renew' : 'robot-service.purchase.title.buy'),
      nzContent: RobotServicePurchaseComponent,
      nzWidth: '960px',
      nzFooter: null,
      nzClosable: true,
      nzMaskClosable: false,
      nzClassName: 'robot-purchase-modal',
      nzData: {
        mode,
        initialStep,
        tenantId: this.modalData?.tenantId ?? null,
        robotId: robot?.robotId ?? null,
        robotServiceId: robot?.robotServiceId ?? null,
        orderId: initialStep === 'payment' ? this.getRobotOrderId(robot) : null,
        name: robot?.name ?? null,
        serviceNumberType: this.modalData?.serviceNumberType ?? null,
        selectedPricing: this.getRobotSelectedPricing(robot),
        trialAvailable: this.trialAvailable,
        trialUsed: this.trialUsed
      }
    });

    purchaseModal.afterClose.subscribe(result => {
      if (result?.refresh) {
        this.loadRobotList();
      }
    });
  }

  openOrderHistory(robot?: any): void {
    const robotId = String(robot?.robotId ?? '').trim();
    if (!robotId) {
      return;
    }
    this.modalService.create({
      nzTitle: this.i18n.fanyi('robot-service.order.history.title'),
      nzContent: RobotOrderHistoryModalComponent,
      nzWidth: '880px',
      nzFooter: null,
      nzClosable: true,
      nzMaskClosable: false,
      nzClassName: 'robot-order-history-modal',
      nzData: {
        robotId,
        name: robot?.name ?? null,
        pageIndex: 0,
        pageSize: 20,
        orderBy: 'createTime',
        direction: 'desc'
      }
    });
  }

  payPendingOrder(robot: any): void {
    if (!this.canPayPendingOrder(robot)) {
      this.message.warning(this.i18n.fanyi('payment.result.countdown.expired'));
      return;
    }
    const orderId = this.getRobotOrderId(robot);
    if (orderId) {
      const existingPayUrl = this.getPaymentUrl(robot);
      if (existingPayUrl) {
        this.openPaymentModal(existingPayUrl, orderId);
        return;
      }
      const apiPath = ConstantUtil.ADMIN_ROBOT_ORDER_V1_PAY.replace('{orderId}', encodeURIComponent(orderId));
      this.http.post(apiPath, this.buildPayPayload(robot, orderId)).subscribe({
        next: res => {
          if (!res?.success) return;
          const payload = res?.data ?? res;
          this.storePaymentSnapshot('robot', orderId, this.getRobotTargetId(robot), payload);
          const payUrl = this.getPaymentUrl(payload);
          if (payUrl) {
            this.openPaymentModal(payUrl, orderId);
            return;
          }
          if (this.isPaymentPaid(payload)) {
            this.loadRobotList();
          }
        },
        error: () => {}
      });
      return;
    }
    this.message.info(this.i18n.fanyi('robot-service.order.pay-coming-soon'));
  }

  cancelPendingOrder(robot: any): void {
    const orderId = this.getRobotOrderId(robot);
    if (!this.hasPendingPayment(robot) || !orderId || this.cancelingOrderId) {
      return;
    }

    const apiPath = ConstantUtil.ADMIN_ROBOT_ORDER_V1_CANCEL.replace('{orderId}', encodeURIComponent(orderId));
    this.cancelingOrderId = orderId;
    this.http
      .post(apiPath, {}, undefined, { silentAllErrors: true })
      .pipe(finalize(() => (this.cancelingOrderId = null)))
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!isSuccess) {
            this.message.warning(res?.msg || this.i18n.fanyi('robot-service.order.cancel-failed'));
            return;
          }

          robot.needPay = false;
          robot.paymentStatus = 'Canceled';
          robot.latestOrderStatus = 'Canceled';
          robot.paymentUrl = null;
          robot.payExpireTime = null;
          this.message.success(this.i18n.fanyi('robot-service.order.cancel-success'));
          this.loadRobotList();
          this.openPurchase(this.hasValidExpireTime(robot) ? 'renew' : 'buy', robot, 'packages');
        },
        error: err => {
          this.message.warning(err?.error?.msg || this.i18n.fanyi('robot-service.order.cancel-failed'));
        }
      });
  }

  private buildPayPayload(robot: any, orderId?: string): any {
    const tenantId = String(this.modalData?.tenantId ?? robot?.tenantId ?? '').trim();
    const urls = this.buildPaymentReturnUrls('robot', {
      tenantId,
      orderId,
      targetId: this.getRobotTargetId(robot)
    });
    const payload: any = {
      tenantId,
      resultURL: urls.resultURL,
      cancelURL: urls.cancelURL
    };
    if (this.aipoolTenantId) {
      payload.aipoolTenantId = this.aipoolTenantId;
    }
    return payload;
  }

  private resolveAipoolTenantId(raw?: any): string {
    return String(raw?.aipoolTenantId ?? '').trim();
  }

  private buildPaymentReturnUrls(
    orderType: PaymentOrderType,
    context: { tenantId?: string; orderId?: string; targetId?: string }
  ): { resultURL: string; cancelURL: string } {
    return {
      resultURL: this.buildPaymentReturnUrl(orderType, 'success', context),
      cancelURL: this.buildPaymentReturnUrl(orderType, 'failed', context)
    };
  }

  private buildPaymentReturnUrl(
    orderType: PaymentOrderType,
    paymentResult: 'success' | 'failed',
    context: { tenantId?: string; orderId?: string; targetId?: string }
  ): string {
    return buildPaymentResultUrl({
      orderType,
      paymentResult,
      tenantId: context.tenantId,
      aipoolTenantId: this.aipoolTenantId,
      orderId: context.orderId,
      targetId: context.targetId,
      sourcePath: this.getSourcePath()
    });
  }

  private getPaymentUrl(raw: any): string | null {
    const payUrl = raw?.paymentUrl ?? null;
    return payUrl ? String(payUrl) : null;
  }

  private openPaymentModal(paymentUrl: string, orderId: string): void {
    const paymentModal = this.modalService.create({
      nzContent: PaymentIframeModalComponent,
      nzWidth: '1200px',
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'payment-iframe-modal',
      nzData: {
        paymentUrl,
        orderId,
        orderType: 'robot'
      }
    });

    paymentModal.afterClose.subscribe(() => {
      this.loadRobotList();
    });
  }

  private isPaymentPaid(raw?: any): boolean {
    const statusValues = [raw?.status, raw?.latestOrderStatus, raw?.orderStatus, raw?.paymentStatus].map(value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    );
    if (statusValues.some(status => ['canceled', 'cancelled', 'failed', 'expired', 'closed', 'replaced'].includes(status))) {
      return false;
    }
    if (statusValues.some(status => ['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(status))) {
      return true;
    }
    if (Number(raw?.status) === 30 || Number(raw?.latestOrderStatus ?? raw?.orderStatus) === 30 || Number(raw?.paymentStatus) === 20) {
      return true;
    }
    return raw?.needPay === false || raw?.payRequired === false;
  }

  private getSourcePath(): string {
    return `${window.location.pathname}${window.location.search}`;
  }

  private getRobotTargetId(robot: any): string {
    return String(robot?.robotId ?? robot?.id ?? '').trim();
  }

  private storePaymentSnapshot(orderType: PaymentOrderType, orderId: string, targetId: string, raw?: any): void {
    storePaymentSessionSnapshot({
      orderType,
      orderId,
      targetId,
      tenantId: String(this.modalData?.tenantId ?? raw?.tenantId ?? '').trim(),
      aipoolTenantId: String(this.aipoolTenantId ?? raw?.aipoolTenantId ?? '').trim() || undefined,
      paymentUrl: this.getPaymentUrl(raw) ?? undefined,
      reserveTimeoutSeconds: raw?.reserveTimeoutSeconds,
      payStartTime: raw?.payStartTime,
      payExpireTime: raw?.payExpireTime,
      orderStatus: raw?.orderStatus,
      paymentStatus: raw?.paymentStatus,
      payRequired: raw?.payRequired
    });
  }

  buyNew(): void {
    this.openPurchase('buy');
  }

  isRenaming(robot: any): boolean {
    return this.renamingRobotId === this.getRobotInstanceKey(robot);
  }

  isRenamingSubmitting(robot: any): boolean {
    return this.renamingSubmittingRobotId === this.getRobotInstanceKey(robot);
  }

  isDeleting(robot: any): boolean {
    return this.deletingRobotId === this.getRobotInstanceKey(robot);
  }

  isAttaching(robot: any): boolean {
    return this.attachingRobotId === this.getRobotInstanceKey(robot);
  }

  isDetaching(robot: any): boolean {
    return this.detachingRobotId === this.getRobotInstanceKey(robot);
  }

  startRename(robot: any): void {
    this.renamingRobotId = this.getRobotInstanceKey(robot);
    this.renameInputValue = String(robot?.name ?? '').trim();
  }

  cancelRename(): void {
    this.renamingRobotId = null;
    this.renameInputValue = '';
  }

  saveRename(robot: any): void {
    const robotKey = this.getRobotInstanceKey(robot);
    const name = String(this.renameInputValue ?? '').trim();
    if (!name) {
      this.message.warning(this.i18n.fanyi('robot-service.my.rename-empty'));
      return;
    }
    if (name === String(robot?.name ?? '').trim()) {
      this.cancelRename();
      return;
    }

    const id = String(robot?.id ?? '').trim();
    if (!id) {
      this.message.error(this.i18n.fanyi('robot-service.my.rename-failed'));
      return;
    }
    const apiPath = this.modalData?.renameApi || ConstantUtil.TENANT_MY_ROBOT_UPDATE;
    const payload: any = { id, name };

    this.renamingSubmittingRobotId = robotKey;
    this.http
      .post(apiPath, payload)
      .pipe(finalize(() => (this.renamingSubmittingRobotId = null)))
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(res?.msg || this.i18n.fanyi('robot-service.my.rename-failed'));
            return;
          }
          robot.name = name;
          this.cancelRename();
          this.message.success(this.i18n.fanyi('robot-service.my.rename-success'));
          this.applyFilter();
        },
        error: () => this.message.error(this.i18n.fanyi('robot-service.my.rename-failed'))
      });
  }

  removeRobot(robot: any): void {
    const robotKey = this.getRobotInstanceKey(robot);
    const name = String(robot?.name ?? robotKey ?? '').trim() || '-';
    const confirmText = this.i18n.fanyi('robot-service.my.delete-confirm').replace('{name}', name);
    if (!window.confirm(confirmText)) return;

    const id = String(robot?.id ?? '').trim();
    if (!id) {
      this.message.error(this.i18n.fanyi('robot-service.my.delete-failed'));
      return;
    }
    const apiPath = this.modalData?.deleteApi || ConstantUtil.TENANT_MY_ROBOT_DELETE;
    const payload: any = { id };

    this.deletingRobotId = robotKey;
    this.http
      .post(apiPath, payload)
      .pipe(finalize(() => (this.deletingRobotId = null)))
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(res?.msg || this.i18n.fanyi('robot-service.my.delete-failed'));
            return;
          }
          if (this.selectedRobotId === robotKey) {
            this.selectedRobotId = null;
            this.selectedRobot = null;
          }
          if (this.mountedRobotId === robotKey) {
            this.mountedRobotId = null;
          }
          this.renamingRobotId = null;
          this.renameInputValue = '';
          this.message.success(this.i18n.fanyi('robot-service.my.delete-success'));
          this.loadRobotList();
        },
        error: () => this.message.error(this.i18n.fanyi('robot-service.my.delete-failed'))
      });
  }

  isBindingLoading(robot: any): boolean {
    return false;
  }

  isBoundElsewhere(robot: any): boolean {
    const bindingStatus = this.getBindingStatusCode(robot);
    if (bindingStatus === 'occupied') {
      return true;
    }
    if (bindingStatus === 'unbound') {
      return false;
    }
    const boundServiceNumberId = this.getBindingServiceNumberId(robot);
    if (!boundServiceNumberId) return false;
    if (this.currentServiceNumberId) {
      return boundServiceNumberId !== this.currentServiceNumberId;
    }
    return true;
  }

  getBindingStatusLabel(robot: any): string {
    if (this.isBindingLoading(robot)) {
      return this.i18n.fanyi('common.loading');
    }
    if (this.hasPendingPayment(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.disabled');
    }
    if (this.isDisabled(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.disabled');
    }
    const bindingStatus = this.getBindingStatusCode(robot);
    if (this.isCurrent(robot) || this.isBoundToCurrentService(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.current');
    }
    if (this.isExpired(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.expired');
    }
    if (bindingStatus === 'occupied' || this.isBoundElsewhere(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.occupied');
    }
    if (bindingStatus === 'unbound') {
      return this.i18n.fanyi('robot-service.my.binding.unbound');
    }
    return this.i18n.fanyi('robot-service.my.binding.unbound');
  }

  getBindingChipText(robot: any): string {
    if (this.hasPendingPayment(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.disabled');
    }
    if (this.isDisabled(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.disabled');
    }
    if (this.isCurrent(robot) || this.isBoundToCurrentService(robot)) {
      const label = this.i18n.fanyi('robot-service.my.binding.legend.current');
      const serviceNumberName = this.getBindingServiceNumberName(robot);
      return serviceNumberName !== '-' ? `${label}・${serviceNumberName}` : label;
    }
    if (this.isExpired(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.expired');
    }
    if (this.isBoundElsewhere(robot)) {
      const label = this.i18n.fanyi('robot-service.my.binding.occupied');
      const serviceNumberName = this.getBindingServiceNumberName(robot);
      return serviceNumberName !== '-' ? `${label}・${serviceNumberName}` : label;
    }
    if (this.isAttachable(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.legend.unbound');
    }
    return this.i18n.fanyi('robot-service.my.binding.unbound');
  }

  getRemainingLabel(robot: any): string {
    if (this.isTrial(robot)) {
      return this.i18n.fanyi('robot-service.my.trial-remaining');
    }
    return this.i18n.fanyi('robot-service.my.remaining');
  }

  getBindingStatusColor(robot: any): string {
    if (this.hasPendingPayment(robot)) {
      return 'default';
    }
    if (this.isDisabled(robot)) {
      return 'default';
    }
    const bindingStatus = this.getBindingStatusCode(robot);
    if (this.isCurrent(robot) || this.isBoundToCurrentService(robot)) {
      return 'processing';
    }
    if (this.isExpired(robot)) {
      return 'default';
    }
    if (bindingStatus === 'occupied' || this.isBoundElsewhere(robot)) {
      return 'orange';
    }
    return 'default';
  }

  getBindingDotClass(robot: any): string {
    if (this.isDisabled(robot)) {
      return 'dot-expired';
    }
    if (this.isCurrent(robot) || this.isBoundToCurrentService(robot)) {
      return 'dot-current';
    }
    if (this.hasPendingPayment(robot)) {
      return 'dot-expired';
    }
    if (this.isExpired(robot)) {
      return 'dot-expired';
    }
    if (this.isBoundElsewhere(robot)) {
      return 'dot-occupied';
    }
    return 'dot-free';
  }

  getAttachButtonLabel(robot: any): string {
    if (this.isBoundElsewhere(robot)) {
      return this.i18n.fanyi('robot-service.my.attach-occupied');
    }
    if (this.isCurrent(robot) || this.isBoundToCurrentService(robot)) {
      return this.i18n.fanyi('robot-service.my.binding.current');
    }
    return this.i18n.fanyi('robot-service.my.attach');
  }

  getTodayUsageText(robot: any): string {
    const usedRaw = robot?.todayUsage;
    const totalRaw = robot?.todayLimit;
    const used = Number(usedRaw);
    const total = Number(totalRaw);
    if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) {
      return '';
    }
    return `${Math.max(0, used)}/${total}`;
  }

  getBindingServiceNumberName(robot: any): string {
    const boundServiceNumberId = this.getBindingServiceNumberId(robot);
    if (!boundServiceNumberId) {
      return '-';
    }
    return String(robot?.bindingServiceNumberName ?? robot?.bindingTarget?.serviceNumberName ?? boundServiceNumberId);
  }

  getBindingServiceNumberTypeLabel(robot: any): string {
    const typeCode = String(robot?.bindingServiceNumberType ?? robot?.bindingTarget?.serviceNumberType ?? '').trim();
    if (!typeCode) return '';
    const map: Record<string, string> = {
      Official: this.i18n.fanyi('servicenumber.type.official'),
      Manage: this.i18n.fanyi('servicenumber.type.manage'),
      Professional: this.i18n.fanyi('servicenumber.type.professional'),
      Normal: this.i18n.fanyi('servicenumber.type.normal'),
      Boss: this.i18n.fanyi('servicenumber.type.boss')
    };
    return map[typeCode] || typeCode;
  }

  private isBoundToCurrentService(robot: any): boolean {
    const bindingStatus = this.getBindingStatusCode(robot);
    if (bindingStatus === 'current') {
      return true;
    }
    if (bindingStatus === 'occupied' || bindingStatus === 'unbound') {
      return false;
    }
    const boundServiceNumberId = this.getBindingServiceNumberId(robot);
    return !!boundServiceNumberId && !!this.currentServiceNumberId && boundServiceNumberId === this.currentServiceNumberId;
  }

  private getBindingStatusCode(robot: any): string {
    return String(robot?.bindingStatus ?? '')
      .trim()
      .toLowerCase();
  }

  private getBindingServiceNumberId(robot: any): string | null {
    const raw = robot?.bindingServiceNumberId ?? robot?.bindingTarget?.serviceNumberId ?? '';
    const value = String(raw ?? '').trim();
    return value || null;
  }

  private getRobotSelectedPricing(robot?: any): SelectedPricing | null {
    const prefix = this.hasPendingPayment(robot) ? 'latest' : 'current';
    const pricingConfigId = String(robot?.[`${prefix}PricingConfigId`] ?? '').trim();
    const pricingItemId = String(robot?.[`${prefix}PricingItemId`] ?? '').trim();
    const pricingCode = String(robot?.[`${prefix}PricingCode`] ?? '').trim();
    if (!pricingItemId && !pricingCode) {
      return null;
    }
    return {
      pricingConfigId,
      pricingItemId,
      pricingCode
    };
  }

  private getRobotOrderId(robot?: any): string | null {
    const value = String(robot?.latestOrderId ?? '').trim();
    return value || null;
  }
}
